import { JSX } from 'react';

import { RootState } from '@/redux/ducks';

import { MerchantType } from './Merchant.types';

export enum Permission {
	TREND_TRANSACTION_ACCESS = 'trend_transaction_access',
	TRANSACTION_ACCESS = 'transaction_access',
	REFUND_LIST_ACCESS = 'refund_list_access',
	CASHBACK_LIST_ACCESS = 'cashback_list_access',
	SEARCH_TRANSACTION_ACCESS = 'search_transaction_access',
	REFUND_ACCESS = 'refund_access',
	SETTLEMENTS_ACCESS = 'settlements_access',
	SETTLEMENTS_REPORTS_ACCESS = 'settlementreports_access',
	REPORTS_ACCESS = 'reports_access',
	PROFILE_ACCESS = 'profile_access',
	QR_CODES_ACCESS = 'qrcodes_access',
	QR_CODE_ADD_MOBILE_ACCESS = 'qrcode_add_mobile_access',
	QR_CODE_REMOVE_MOBILE_ACCESS = 'qrcode_remove_mobile_access',
	USERS_ACCESS = 'users_access',
	USERS_ADD_ACCESS = 'users_add_access',
	USERS_EDIT_ACCESS = 'users_edit_access',
	BATCH_RECON_ACCESS = 'batch_recon_access',
	BATCH_REFUND_ACCESS = 'batch_refund_access',
	DOWNTIME_ACCESS = 'downtime_access',
	MASTER_SETTLEMENT_REPORTS_ACCESS = 'master_settlement_reports_access',
	STORE_UPDATE_ACCESS = 'store_update_access',
	STORE_ACCESS = 'store_access',
	HELP_DESK_VIEW_ACCESS = 'help_desk_view_access',
	TRANSACTION_REPORTS_ACCESS = 'transaction_reports_access',
	APP_DETAILS = 'app_details',
	APP_LIST = 'apps_list',
	MERCHANT_SUPPORT_CREATE_TICKET = 'merchant_support_create_ticket',
	MERCHANT_MIGRATION_P2ML = 'merchant_migration_p2ml',
	MERCHANT_PROFILE_UPDATE = 'merchant_profile_update',
	QR_UPDATE_ACCESS = 'qr_update_access',
	VIEW_ESCALATION_ACCESS = 'view_escalation_access',
	VIEW_BATCH_REFUND_ACCESS = 'view_batch_refund_access',
	REFUND_REPORTS_ACCESS = 'refund_reports_access',
	BATCH_ESCALATION_ACCESS = 'batch_escalation_access',
	APP_CREATION_MAKER = 'app-creation-maker',
	APP_REVIEW_MAKER = 'app-review-maker',
	APP_RELEASE_MAKER = 'app-release-maker',
	APP_UPDATE_MAKER = 'app-update-maker',
	ONLINE_MERCHANT_ONBOARDING = 'onlineMerchantOnboarding',
	paylink_read_access = 'paylink_read_access',
	paylink_create_access = 'paylink_create_access',
	paylink_update_access = 'paylink_update_access',
	PAYLINK_READ_ACCESS = 'PAYLINK_READ_ACCESS',
	PAYLINK_MANAGE_ACCESS = 'PAYLINK_MANAGE_ACCESS',
	OMX_CONFIG_UPDATE = 'OMX_CONFIG_UPDATE',
	PRIVATE_KEY_READ_ACCESS = 'PRIVATE_KEY_READ_ACCESS',
	QMC_REPORT_ACCESS = 'qmc_report_access',
	MERCHANT_BANK_ACCOUNT_ACCESS = 'MERCHANT_BANK_ACCOUNT_ACCESS',
	VENDOR_ACCESS = 'VENDOR_ACCESS',
}

export interface IRoutePermission {
	online?: Permission[];
	offline?: Permission[];
	fastag?: Permission[];
	chimera?: { [key: string]: boolean };
	isUPIVerified?: boolean;
	isB2bPgActivated?: boolean;
	isSuperMerchant?: boolean;
	isSplitSettlementEnabled?: boolean;
}

export enum RoutePath {
	HOME = '/',
	PG_REGISTER = '/pg/register',
	REGISTER = '/register',
	LOGIN = '/login',
	DASHBOARD = '/dashboard',
	SR_DASHBOARD = '/sr-dashboard',
	APPLINK = '/applink',
	TRANSACTIONS = '/transactions',
	TRANSACTIONS_HISTORY = 'history',
	LIVE_TRANSACTIONS = 'live',
	REFUNDS = 'refunds',
	REPORTS = '/reports',
	TRANSACTION_REPORTS = 'transaction-reports',
	DAILY_TRANSACTION_REPORTS = 'transaction-daily-reports',
	REFUND_REPORTS = 'refund-reports',
	DAILY_REFUND_REPORTS = 'refund-daily-reports',
	CUSTOM_SETTLEMENT_REPORT = 'custom-settlement-reports',
	QMC_REPORTS = 'qmc-reports',
	TRANSACTION_DETAILS = '/transactions/details/:unitId',
	SETTLEMENTS = '/settlements',
	PAST_REFUNDS = '/refunds/past-refunds',
	INITIATE_REFUNDS = '/refunds/initiate',
	QR = '/qr',
	STORES = '/store/list',
	STORE_DETAILS = '/store/details/:storeId/:merchantId',
	MANAGE_USERS = '/team',
	FAQS = '/faq',
	USER_PROFILE = '/profile',
	OFFLINE_PAYLINKS = '/pay-links',
	ONLINE_PAYLINKS = '/pay-links-new',
	DEVELOPER_SETTINGS = '/developer-settings',
	API_KEYS = 'api-keys',
	WEBHOOKS = 'webhooks',
	COMPLETE_KYC = '/complete-kyc',
	PG_ACTIVATION = '/pg-activation',
	USER_VERIFY = '/user-verify',
	RESET_PASSWORD = '/set-password',
	LOGIN_REDIRECTION = '/login-redirection',
	NOT_FOUND = '/404',
	SPLIT_SETTLEMENTS = '/split-settlements',
	SELF_ACCOUNTS = 'self-accounts',
	SELF_ONBOARDING = '/self-onboarding',
	SELF_ONBOARDING_BANK_DETAILS = '/self-onboarding/:bankId',
	VENDOR_ACCOUNTS = 'vendor-accounts',
	VENDOR_ONBOARDING = '/vendor-onboarding',
	VENDOR_ONBOARDING_VENDOR_DETAILS = '/vendor-onboarding/:vendorId',
}

export interface ILayoutInfo {
	showSideNavbar: boolean;
	showTopNavbar: boolean;
	showFooter: boolean;
	withContentPadding: boolean;
}
export interface IRouteData {
	layoutInfo?: ILayoutInfo;
	testModeSupported?: boolean;
}

export enum ChimeraAccessKey {
	PAYLINK_ENABLED = 'PAYLINK_ENABLED',
	ONLINE_PAYLINKS_HIDE = 'ONLINE_PAYLINKS_HIDE',
	NCMC_MERCHANT = 'NCMC_MERCHANT',
	QMC_REPORT_ENABLED = 'QMC_REPORT_ENABLED',
	SR_DASHBOARD_ENABLED = 'SR_DASHBOARD_ENABLED',
	VENDOR_ONBOARDING = 'VENDOR_ONBOARDING',
}

export enum RouteId {
	REGISTER_PAGE = 'REGISTER_PAGE',
	APPLINK = 'APPLINK',
	USER_VERIFY_PAGE = 'USER_VERIFY_PAGE',
	PG_REGISTER_PAGE = 'PG_REGISTER_PAGE',
	MO_REGISTER_PAGE = 'MO_REGISTER_PAGE',
	MO_REGISTER_V2_PAGE = 'MO_REGISTER_V2_PAGE',
	LOGIN_PAGE = 'LOGIN_PAGE',
	DASHBOARD_PAGE = 'DASHBOARD_PAGE',
	SR_DASHBOARD_PAGE = 'SR_DASHBOARD_PAGE',
	HOME = 'HOME_PAGE',
	TRANSACTIONS_PAGE = 'TRANSACTIONS_PAGE',
	TRANSACTIONS_HISTORY_PAGE = 'TRANSACTIONS_HISTORY_PAGE',
	LIVE_TRANSACTIONS_PAGE = 'LIVE_TRANSACTIONS_PAGE',
	TRANSACTION_DETAILS_PAGE = 'TRANSACTION_DETAILS_PAGE',
	TRANSACTION_REPORTS_PAGE = 'TRANSACTION_REPORTS_PAGE',
	DAILY_TRANSACTION_REPORTS_PAGE = 'DAILY_TRANSACTION_REPORTS_PAGE',
	CUSTOM_SETTLEMENT_REPORTS_PAGE = 'CUSTOM_SETTLEMENT_REPORTS_PAGE',
	REFUND_REPORTS_PAGE = 'REFUND_REPORTS_PAGE',
	DAILY_REFUND_REPORTS_PAGE = 'DAILY_REFUND_REPORTS_PAGE',
	REPORTS_PAGE = 'REPORTS_PAGE',
	SETTLEMENTS_PAGE = 'SETTLEMENTS_PAGE',
	REFUNDS_PAGE = 'REFUNDS',
	QR_PAGE = 'QR_PAGE',
	ENABLED_QRS_PAGE = 'ENABLED_QRS_PAGE',
	DISABLED_QRS_PAGE = 'DISABLED_QRS_PAGE',
	STORES_PAGE = 'STORES_PAGE',
	STORES_DETAILS_PAGE = 'STORES_DETAILS_PAGE',
	MANAGE_USERS_PAGE = 'MANAGE_USERS_PAGE',
	FAQS_PAGE = 'FAQS_PAGE',
	USER_PROFILE_PAGE = 'USER_PROFILE_PAGE',
	OFFLINE_PAYLINKS_PAGE = 'OFFLINE_PAYLINKS_PAGE',
	ONLINE_PAYLINKS_PAGE = 'ONLINE_PAYLINKS_PAGE',
	DEVELOPER_SETTINGS_PAGE = 'DEVELOPER_SETTINGS_PAGE',
	API_KEYS = 'API_KEYS_PAGE',
	WEBHOOKS = 'WEBHOOKS_PAGE',
	COMPLETE_KYC_PAGE = 'COMPLETE_KYC_PAGE',
	RESET_PASSWORD_PAGE = 'RESET_PASSWORD',
	PG_ACTIVATION_PAGE = 'PG_ACTIVATION_PAGE',
	LOGIN_REDIRECTION_PAGE = 'LOGIN_REDIRECTION_PAGE',
	NOT_FOUND_PAGE = 'NOT_FOUND_PAGE',
	SPLIT_SETTLEMENTS_PAGE = 'SPLIT_SETTLEMENTS_PAGE',
	SELF_ACCOUNTS_PAGE = 'SELF_ACCOUNTS_PAGE',
	SELF_ONBOARDING_PAGE = 'SELF_ONBOARDING_PAGE',
	QMC_REPORTS_PAGE = 'QMC_REPORTS_PAGE',
	SELF_ONBOARDING_BANK_DETAILS_PAGE = 'SELF_ONBOARDING_BANK_DETAILS_PAGE',
	VENDOR_ONBOARDING_VENDOR_DETAILS_PAGE = 'VENDOR_ONBOARDING_VENDOR_DETAILS_PAGE',
	VENDOR_ACCOUNTS_PAGE = 'VENDOR_ACCOUNTS_PAGE',
	VENDOR_ONBOARDING_PAGE = 'VENDOR_ONBOARDING_PAGE',
}

export interface IPFBRoute {
	id: RouteId;
	isAuthenticated?: boolean;
	path: string;
	permissions: IRoutePermission;
	allowedMerchants: MerchantType[];
	element?: JSX.Element;
	children?: IPFBRoute[];
	routeData?: IRouteData;
}

export type RouteNameType = keyof Pick<
	RootState['userConfig'],
	| 'dashboard'
	| 'txnHistoryList'
	| 'paylinks'
	| 'refundsList'
	| 'settlements'
	| 'developer-settings'
	| 'pay-links-new'
	| 'myStores'
	| 'qr'
>;
